import { getPartnerLabel, PartnerType } from "@/app/utils/partners.enum";

import { withCdn } from "@/app/utils/withCdn";
import type { ProductDetail } from "@/app/services/configService/getProductsConfig";

export const SINGLE_COMMISSION_PARTNERS: PartnerType[] = [
  PartnerType.PAN,
  PartnerType.C6,
  PartnerType.CREFAZ_CLT,
  PartnerType.CREFISA,
  PartnerType.CREFISA_INSS,
];

export const isSingleCommissionPartner = (
  partner?: PartnerType | string | null,
): boolean =>
  !!partner && SINGLE_COMMISSION_PARTNERS.includes(partner as PartnerType);

export const MONEY_COMMISSION_PARTNERS: PartnerType[] = [PartnerType.SALVCAR];

export const isMoneyCommissionPartner = (
  partner?: PartnerType | string | null,
): boolean =>
  !!partner && MONEY_COMMISSION_PARTNERS.includes(partner as PartnerType);

export const stripCltPrefix = (label: string) =>
  label.replace(/^\s*CLT\s*-\s*/i, "").trim() || label;

// TODO: remover mock
export const partnerDescriptions: Record<PartnerType, string> = {
  [PartnerType.FGTS]: "Antecipação do saque-aniversário do FGTS",
  [PartnerType.CREFAZ]: "Crédito pessoal com débito na fatura de energia",
  [PartnerType.SALVCAR]: "Proteção veicular",
  [PartnerType.CREFISA]: "Bolsa família",
  [PartnerType.CREFISA_INSS]: "Crédito pessoal",
  [PartnerType.PAN]: "Consignado privado",
  [PartnerType.CLT]: "Consignado CLT",
  [PartnerType.C6]: "Consignado privado",
  [PartnerType.CREFAZ_CLT]: "Consignado CLT",
};

export const getPartnerImage = (partner: PartnerType): string => {
  const imageMap: Record<PartnerType, string> = {
    [PartnerType.FGTS]: "/fgts.png",
    [PartnerType.CREFAZ]: "/crefaz.png",
    [PartnerType.SALVCAR]: "/salvcar.png",
    [PartnerType.CREFISA]: "/crefisa.png",
    [PartnerType.CREFISA_INSS]: "/crefisa_inss.png",
    [PartnerType.PAN]: "/pan.png",
    [PartnerType.CLT]: "/clt.svg",
    [PartnerType.C6]: "/c6Bank.png",
    [PartnerType.CREFAZ_CLT]: "/crefaz.png",
  };

  return imageMap[partner];
};

export const getPartnerBgColor = (partner: PartnerType): string => {
  const bgColorMap: Record<PartnerType, string> = {
    [PartnerType.FGTS]: "#036039",
    [PartnerType.CREFAZ]: "#F77325",
    [PartnerType.SALVCAR]: "#AA63FF",
    [PartnerType.CREFISA]: "#02BCE8",
    [PartnerType.CREFISA_INSS]: "#00329A",
    [PartnerType.PAN]: "#414141",
    [PartnerType.CLT]: "#4AFFC5",
    [PartnerType.C6]: "#111111",
    [PartnerType.CREFAZ_CLT]: "#E6530A",
  };

  return bgColorMap[partner];
};

export const getPartnerDisplay = (
  partner: PartnerType,
  product?: ProductDetail,
) => ({
  label:
    product?.label || product?.externalData?.title || getPartnerLabel(partner),
  description:
    product?.subtitle ||
    product?.externalData?.subtitle ||
    partnerDescriptions[partner],
  image:
    (product?.image && withCdn(product.image)) ||
    product?.externalData?.image ||
    getPartnerImage(partner),
  color:
    product?.color ||
    product?.externalData?.color ||
    getPartnerBgColor(partner),
});

export const getPartnerCardStyle = (
  partner: PartnerType | string,
): { bg: string; text: string; border: string } => {
  const styleMap: Record<string, { bg: string; text: string; border: string }> =
    {
      [PartnerType.FGTS]: { bg: "#036039", text: "#ffffff", border: "#024a2c" },
      [PartnerType.CREFAZ]: {
        bg: "#F77325",
        text: "#ffffff",
        border: "#d5611e",
      },
      [PartnerType.SALVCAR]: {
        bg: "#AA63FF",
        text: "#ffffff",
        border: "#9450e0",
      },
      [PartnerType.CREFISA]: {
        bg: "#02BCE8",
        text: "#ffffff",
        border: "#02a3ca",
      },
      [PartnerType.CREFISA_INSS]: {
        bg: "#00329A",
        text: "#ffffff",
        border: "#00329A",
      },
      [PartnerType.PAN]: { bg: "#414141", text: "#ffffff", border: "#2e2e2e" },
      [PartnerType.CLT]: { bg: "#4AFFC5", text: "#ffffff", border: "#4AFFC5" },
      [PartnerType.C6]: { bg: "#111111", text: "#ffffff", border: "#111111" },
      [PartnerType.CREFAZ_CLT]: {
        bg: "#E6530A",
        text: "#ffffff",
        border: "#c44508",
      },
    };

  return (
    styleMap[partner] || { bg: "#f3f4f6", text: "#374151", border: "#d1d5db" }
  );
};
